import * as React from 'react';
import { cn } from '../lib/utils.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table.js';

// ─── Types ────────────────────────────────────────────────────

export interface RowPreviewColumn<T extends Record<string, unknown>> {
  /** Key into the row data object. */
  key: keyof T & string;
  /** Column header label — also used as the key for highlightFields and fieldOverrides. */
  label: string;
}

export interface RowPreviewPanelProps<T extends Record<string, unknown>> {
  /**
   * Section heading displayed above the mini-table.
   * Caller provides translated/formatted string (i18n rule).
   */
  label: string;
  columns: RowPreviewColumn<T>[];
  row: T;
  /**
   * Column labels to highlight.
   * When `highlightColor` is 'success': green.
   * When `highlightColor` is 'issue': red for 'error' severity, amber for 'warning'.
   */
  highlightFields?: string[];
  /** Coloring mode for highlighted cells. @default 'issue' */
  highlightColor?: 'issue' | 'success';
  /**
   * Per-field severity used when `highlightColor === 'issue'`.
   * Keyed by column label. Defaults to 'error' when absent.
   */
  issueSeverities?: Record<string, 'error' | 'warning'>;
  /**
   * Override displayed cell values, keyed by column label.
   * Useful for showing a corrected value in place of the original.
   */
  fieldOverrides?: Record<string, string>;
  /**
   * Number of `<td>` columns the wrapper `<td>` should span in the outer table.
   * @default 6
   */
  colSpan?: number;
  /** Placeholder shown when the cell value is an empty string. @default 'brak' */
  emptyLabel?: string;
  headerSlot?: React.ReactNode;
  controls?: React.ReactNode;
  className?: string;
  /** data-testid applied to the outer `<tr>`. */
  testId?: string;
}

// ─── Helpers ─────────────────────────────────────────────────

const COL_MIN_W = 'min-w-[100px]';

function cellCn(
  label: string,
  value: string,
  highlightFields: string[],
  highlightColor: 'issue' | 'success',
  issueSeverities: Record<string, 'error' | 'warning'>,
): string {
  const base = `${COL_MIN_W} px-3 py-2 font-mono whitespace-nowrap border-r border-border/40 last:border-r-0`;
  const empty = value === '' ? 'text-muted-foreground italic' : '';

  if (!highlightFields.includes(label)) return cn(base, 'text-foreground', empty);

  if (highlightColor === 'success')
    return cn(base, 'bg-green-50 text-green-700 font-semibold', empty);

  const sev = issueSeverities[label] ?? 'error';
  return sev === 'error'
    ? cn(base, 'bg-red-50 text-red-700 font-semibold', empty)
    : cn(base, 'bg-amber-50 text-amber-700 font-semibold', empty);
}

function headerCn(
  label: string,
  highlightFields: string[],
  highlightColor: 'issue' | 'success',
  issueSeverities: Record<string, 'error' | 'warning'>,
): string {
  const base = `${COL_MIN_W} text-left px-3 py-1.5 font-medium text-muted-foreground whitespace-nowrap border-r border-border/40 last:border-r-0`;

  if (!highlightFields.includes(label)) return base;

  if (highlightColor === 'success') return cn(base, 'bg-green-50 text-green-700');

  const sev = issueSeverities[label] ?? 'error';
  return sev === 'error'
    ? cn(base, 'bg-red-50 text-red-700')
    : cn(base, 'bg-amber-50 text-amber-700');
}

// ─── Component ───────────────────────────────────────────────

export function RowPreviewPanel<T extends Record<string, unknown>>({
  label,
  columns,
  row,
  highlightFields = [],
  highlightColor = 'issue',
  issueSeverities = {},
  fieldOverrides = {},
  colSpan = 6,
  emptyLabel = 'brak',
  headerSlot,
  controls,
  className,
  testId,
}: RowPreviewPanelProps<T>) {
  return (
    <tr data-testid={testId}>
      {/*
        max-w-0 + overflow-hidden prevents this td from widening the outer table.
        border-b-[3px] creates the strong group-termination divider.
      */}
      <td
        colSpan={colSpan}
        className={cn('p-0 max-w-0 overflow-hidden border-b-[3px] border-border/40', className)}
      >
        <div className="border-t border-border/30 bg-white">
          <div className="px-4 py-3">
            {headerSlot}
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                {label}
              </p>
              {controls}
            </div>
            <div className="overflow-x-auto">
              <Table className="text-[12px] border border-border/50 rounded-md overflow-hidden w-max">
                <TableHeader>
                  <TableRow className="border-b border-border/50">
                    {columns.map((col) => (
                      <TableHead
                        key={col.key}
                        className={headerCn(
                          col.label,
                          highlightFields,
                          highlightColor,
                          issueSeverities,
                        )}
                      >
                        {col.label}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    {columns.map((col) => {
                      const rawValue = String(row[col.key] ?? '');
                      const value = fieldOverrides[col.label] ?? rawValue;
                      return (
                        <TableCell
                          key={col.key}
                          className={cellCn(
                            col.label,
                            value,
                            highlightFields,
                            highlightColor,
                            issueSeverities,
                          )}
                        >
                          {value !== '' ? value : emptyLabel}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}
